import { useState, useRef, useCallback, useEffect } from 'react';
import { message } from 'antd';

export const useCamera = () => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [capturedImages, setCapturedImages] = useState([]);
  const [stream, setStream] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = useCallback(async () => {
    setIsStartingCamera(true);
    setCameraError(null);
    
    try {
      console.log('Starting camera...');
      
      // Small delay to ensure modal is fully rendered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Try to get back camera first, fallback to any camera
      let mediaStream;
      try {
        console.log('Trying back camera...');
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment', // Use back camera if available
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        console.log('Back camera successful');
      } catch (envError) {
        console.log('Back camera not available, trying any camera');
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        console.log('Any camera successful');
      }
      
      console.log('Media stream obtained:', mediaStream);
      setStream(mediaStream);
      setIsCapturing(true);
      console.log('Camera setup complete');
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions and try again.');
      message.error('Unable to access camera. Please check permissions and try again.');
    } finally {
      setIsStartingCamera(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCapturing(false);
  }, [stream]);

  // Compression settings: max dimension and JPEG quality to keep mobile photos under backend limit
  const MAX_IMAGE_DIMENSION = 1920;
  const JPEG_QUALITY = 0.65;
  const MAX_FILE_SIZE_MB = 3;

  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');

      let width = video.videoWidth || video.clientWidth || 640;
      let height = video.videoHeight || video.clientHeight || 480;

      // Resize if dimensions exceed max (mobile cameras often produce 4K+ images)
      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width > height) {
          height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
          width = MAX_IMAGE_DIMENSION;
        } else {
          width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
          height = MAX_IMAGE_DIMENSION;
        }
      }

      canvas.width = width;
      canvas.height = height;
      context.drawImage(video, 0, 0, width, height);

      const createBlobWithQuality = (quality, retriesLeft = 2) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            message.error('Failed to capture image');
            return;
          }
          const sizeMB = blob.size / 1024 / 1024;
          if (sizeMB > MAX_FILE_SIZE_MB && retriesLeft > 0 && quality > 0.4) {
            createBlobWithQuality(Math.max(0.4, quality - 0.15), retriesLeft - 1);
            return;
          }
          const file = new File([blob], `captured-image-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          const previewCanvas = document.createElement('canvas');
          previewCanvas.width = Math.min(width, 400);
          previewCanvas.height = Math.min(height, (400 * height) / width);
          previewCanvas.getContext('2d').drawImage(canvas, 0, 0, previewCanvas.width, previewCanvas.height);
          const preview = previewCanvas.toDataURL('image/jpeg', 0.7);
          setCapturedImages(prev => [
            ...prev,
            { file, preview, id: Date.now() }
          ]);
          message.success('Image captured successfully!');
        }, 'image/jpeg', quality);
      };

      createBlobWithQuality(JPEG_QUALITY);
    } else {
      message.error('Camera not ready for capture');
    }
  }, []);

  const removeCapturedImage = useCallback((id) => {
    setCapturedImages(prev => prev.filter(img => img.id !== id));
  }, []);

  const previewCapturedImage = useCallback((image) => {
    return image.preview;
  }, []);

  // Cleanup camera on component unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Setup video when stream and video element are available
  useEffect(() => {
    if (stream && videoRef.current && isCapturing) {
      console.log('Setting up video with stream...');
      const video = videoRef.current;
      
      // Set the video source
      video.srcObject = stream;
      
      // Ensure video doesn't receive focus
      video.setAttribute('tabindex', '-1');
      video.setAttribute('aria-hidden', 'true');
      
      // Wait for the video to load
      video.onloadedmetadata = () => {
        console.log('Video metadata loaded, attempting to play...');
        console.log('Video dimensions on metadata loaded:', {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          clientWidth: video.clientWidth,
          clientHeight: video.clientHeight
        });
        video.play().then(() => {
          console.log('Video playing successfully');
          console.log('Video dimensions after play:', {
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            clientWidth: video.clientWidth,
            clientHeight: video.clientHeight
          });
        }).catch((playError) => {
          console.error('Video play error:', playError);
          // Try again after a short delay
          setTimeout(() => {
            video.play().catch(console.error);
          }, 100);
        });
      };
      
      video.oncanplay = () => {
        console.log('Video can play');
      };
      
      video.onerror = (e) => {
        console.error('Video error:', e);
      };
      
      // Force play after a short delay as fallback
      setTimeout(() => {
        if (video.paused) {
          console.log('Video is paused, attempting to play...');
          video.play().catch(console.error);
        }
      }, 500);
    }
  }, [stream, isCapturing]);

  // Simple focus management for camera functionality
  useEffect(() => {
    if (isCapturing && videoRef.current) {
      // Ensure video doesn't steal focus
      const video = videoRef.current;
      video.blur();
    }
  }, [isCapturing]);

  return {
    isCapturing,
    isStartingCamera,
    capturedImages,
    stream,
    cameraError,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    captureImage,
    removeCapturedImage,
    previewCapturedImage
  };
};
